import { getApiFilterKey, getGlobalPeriodColumnKeys } from "../pl-by-program/plTimeFilterUtils";
import { sumRows } from "../tableTotalUtils";
import { metricTotalValue, WATERFALL_MONTH_KEYS } from "./waterfallTableUtils";
import { toReportApiFilters, isAllFilterSelected, withPeriodFiltersForDownload, resolvePopEndDateForApi } from "../filterRelationshipUtils";

/** Global filters for Waterfall/EAC and Scenario MoM APIs — month/quarter are client-side only. */
export function buildWaterfallEacApiFilters(filters) {
  if (!filters) return {};
  const { monthName, quarter, ...rest } = filters;
  return toReportApiFilters(rest);
}

/** Same as API filters, plus month/quarter for download endpoints. */
export function buildWaterfallEacDownloadFilters(filters) {
  return withPeriodFiltersForDownload(filters, buildWaterfallEacApiFilters(filters));
}

const FORECAST_SUMMARY_ARRAY_KEYS = [
  "contractId", "projectId", "bu", "orgId", "contractType", "clinLvl2", "contractName", "customerName",
  "sector", "competitionType", "awardType", "businessType",
];

/** Filters for POST /reports/forecast_summary/monthly — month/quarter are client-side only. */
export function buildForecastSummaryApiFilters(filters) {
  if (!filters) return {};
  const { monthName, quarter, popEndDate, ...rest } = filters;
  const out = toReportApiFilters(rest, ["year", ...FORECAST_SUMMARY_ARRAY_KEYS]);
  const popRange = resolvePopEndDateForApi(
    isAllFilterSelected(popEndDate) ? null : popEndDate,
  );
  if (popRange) out.popEndDate = popRange;
  return out;
}

/** Same as API filters, plus month/quarter for the download endpoint. */
export function buildForecastSummaryDownloadFilters(filters) {
  return withPeriodFiltersForDownload(filters, buildForecastSummaryApiFilters(filters));
}

export { getApiFilterKey, getGlobalPeriodColumnKeys };

function resolveTotalMonthKeys(monthKeys) {
  return monthKeys?.length ? monthKeys : WATERFALL_MONTH_KEYS;
}

/** EAC summary column value for the active month window (global + column picker). */
export function waterfallEacFieldValue(row, fieldKey, monthKeys) {
  const months = resolveTotalMonthKeys(monthKeys);
  if (fieldKey === "eacRev") return metricTotalValue(row.contractRevenue, false, months);
  if (fieldKey === "eacGP") return metricTotalValue(row.grossMargin, false, months);
  if (fieldKey === "eacNP") return metricTotalValue(row.netProfitFee, false, months);
  if (fieldKey === "eacGPPct") {
    const rev = metricTotalValue(row.contractRevenue, false, months);
    const gm = metricTotalValue(row.grossMargin, false, months);
    return resolveMarginPct(rev, gm);
  }
  if (fieldKey === "eacNPPct") {
    const rev = metricTotalValue(row.contractRevenue, false, months);
    const np = metricTotalValue(row.netProfitFee, false, months);
    return resolveMarginPct(rev, np);
  }
  return row[fieldKey];
}

function resolveMarginPct(rev, margin) {
  if (rev == null && margin == null) return null;
  if (rev == null) return null;
  const m = margin ?? 0;
  if (!rev) return 0;
  return (m / rev) * 100;
}

/** Month cell value for a waterfall measure group (computes % from $ when needed). */
export function waterfallMonthValue(row, group, monthKey) {
  if (!group.isPct) return (row[group.key] || {})[monthKey];
  const stored = (row[group.key] || {})[monthKey];
  if (stored != null && stored !== "") return stored;
  if (group.key === "grossMarginPct") {
    return resolveMarginPct(row.contractRevenue?.[monthKey], row.grossMargin?.[monthKey]);
  }
  if (group.key === "netMarginPct") {
    return resolveMarginPct(row.contractRevenue?.[monthKey], row.netProfitFee?.[monthKey]);
  }
  return stored ?? null;
}

/** Sum or weighted % total for a waterfall measure group row. */
export function waterfallGroupTotal(row, group, monthKeys) {
  const months = resolveTotalMonthKeys(monthKeys);
  if (!group.isPct) return metricTotalValue(row[group.key], false, months);
  if (group.key === "grossMarginPct") {
    const rev = metricTotalValue(row.contractRevenue, false, months);
    const gm = metricTotalValue(row.grossMargin, false, months);
    return resolveMarginPct(rev, gm);
  }
  if (group.key === "netMarginPct") {
    const rev = metricTotalValue(row.contractRevenue, false, months);
    const np = metricTotalValue(row.netProfitFee, false, months);
    return resolveMarginPct(rev, np);
  }
  return metricTotalValue(row[group.key], true, months);
}

/** @deprecated Prefer waterfallEacFieldValue / waterfallGroupTotal at display time. */
export function applyPeriodToWaterfallRow(row, periodMonthKeys) {
  if (!periodMonthKeys?.length) return row;
  return {
    ...row,
    eacRev: waterfallEacFieldValue(row, "eacRev", periodMonthKeys),
    eacGP: waterfallEacFieldValue(row, "eacGP", periodMonthKeys),
    eacNP: waterfallEacFieldValue(row, "eacNP", periodMonthKeys),
    eacGPPct: waterfallEacFieldValue(row, "eacGPPct", periodMonthKeys),
    eacNPPct: waterfallEacFieldValue(row, "eacNPPct", periodMonthKeys),
  };
}

export function applyPeriodToWaterfallRows(rows, periodMonthKeys) {
  if (!periodMonthKeys?.length) return rows;
  return rows.map((row) => applyPeriodToWaterfallRow(row, periodMonthKeys));
}

function sumMeasureMap(rows, measureKey, monthKeys) {
  const map = {};
  for (const mk of monthKeys) {
    map[mk] = sumRows(rows, (r) => (r[measureKey] || {})[mk]);
  }
  return map;
}

/** Sum filtered contract rows into a synthetic grand-total row. */
export function aggregateWaterfallRows(rows, monthKeys) {
  if (!rows?.length) return null;
  const months = resolveTotalMonthKeys(monthKeys);
  const contractRevenue = sumMeasureMap(rows, "contractRevenue", months);
  const grossMargin = sumMeasureMap(rows, "grossMargin", months);
  const directLabor = sumMeasureMap(rows, "directLabor", months);
  const subcontractor = sumMeasureMap(rows, "subcontractor", months);
  const odcs = sumMeasureMap(rows, "odcs", months);
  const netProfitFee = sumMeasureMap(rows, "netProfitFee", months);
  const grossMarginPct = {};
  const netMarginPct = {};
  for (const mk of months) {
    const rev = contractRevenue[mk];
    const gm = grossMargin[mk];
    const np = netProfitFee[mk];
    grossMarginPct[mk] = resolveMarginPct(rev, gm);
    netMarginPct[mk] = resolveMarginPct(rev, np);
  }
  return {
    projectNum: "",
    contractName: "Total",
    customerName: "",
    bu: "",
    pm: "",
    popStart: "",
    popEnd: "",
    contractType: "",
    awardType: "",
    competitionType: "",
    tcvCeiling: sumRows(rows, (r) => r.tcvCeiling),
    tcvFunded: sumRows(rows, (r) => r.tcvFunded),
    isTotal: true,
    contractRevenue,
    directLabor,
    subcontractor,
    odcs,
    grossMargin,
    grossMarginPct,
    netProfitFee,
    netMarginPct,
  };
}

/** Sum filtered rows for EAC variance view (live / selected / variance blocks). */
export function aggregateEacVarianceRows(rows, measures) {
  if (!rows?.length) return null;
  const sumBlock = (blockKey) => {
    const block = {};
    for (const m of measures) {
      if (!m.isPct) {
        block[m.key] = sumRows(rows, (r) => r[blockKey]?.[m.key]);
      }
    }
    if (block.contractRevenue != null && block.grossMargin != null) {
      block.grossMarginPct = resolveMarginPct(block.contractRevenue, block.grossMargin);
    }
    if (block.contractRevenue != null && block.netProfit != null) {
      block.netMarginPct = resolveMarginPct(block.contractRevenue, block.netProfit);
    }
    return block;
  };
  const liveEac = sumBlock("liveEac");
  const selected = sumBlock("selected");
  const variance = {};
  for (const m of measures) {
    variance[m.key] = (liveEac[m.key] ?? 0) - (selected[m.key] ?? 0);
  }
  return {
    projectNum: "",
    contractName: "Total",
    customerName: "",
    bu: "",
    pm: "",
    popStart: "",
    popEnd: "",
    contractType: "",
    awardType: "",
    competitionType: "",
    tcvCeiling: sumRows(rows, (r) => r.tcvCeiling),
    tcvFunded: sumRows(rows, (r) => r.tcvFunded),
    isTotal: true,
    liveEac,
    selected,
    variance,
  };
}

/** Sum filtered rows for three-year waterfall view. */
export function aggregateThreeYearRows(rows, yearGroups, yearMetrics) {
  if (!rows?.length) return null;
  const out = {
    projectNum: "",
    contractName: "Total",
    customerName: "",
    bu: "",
    pm: "",
    popStart: "",
    popEnd: "",
    contractType: "",
    tcvCeiling: sumRows(rows, (r) => r.tcvCeiling),
    tcvFunded: sumRows(rows, (r) => r.tcvFunded),
    isTotal: true,
  };
  for (const yg of yearGroups) {
    const block = {};
    for (const m of yearMetrics) {
      if (!m.isPct) {
        block[m.key] = sumRows(rows, (r) => r[yg.key]?.[m.key]);
      }
    }
    if (block.contractRevenue != null && block.grossMargin != null) {
      block.grossMarginPct = resolveMarginPct(block.contractRevenue, block.grossMargin);
    }
    if (block.contractRevenue != null && block.netProfit != null) {
      block.netMarginPct = resolveMarginPct(block.contractRevenue, block.netProfit);
    }
    out[yg.key] = block;
  }
  return out;
}
